import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { ArrowLeft, Warning, Flower } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function MemorialSuggest() {
  useSeo("Suggest a Memorial", "Know a classmate, teacher or alumnus who has passed away? Help us preserve their memory.");
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", institution: "", class_batch: "", approx_years: "", relationship: "", known_info: "", source: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center" data-testid="memorial-suggest-authwall">
        <p className="text-muted-foreground">Please <Link to="/login" className="text-secondary hover:underline">sign in</Link> to suggest a memorial.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Please enter the person's name"); return; }
    setLoading(true);
    try {
      await api.post("/memorials/suggest", f);
      toast.success("Thank you — your suggestion has been sent for review");
      nav("/memorials");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-xl px-5 py-12" data-testid="memorial-suggest-page">
      <Link to="/memorials" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Obituaries &amp; Memorials</Link>
      <div className="mt-4 flex items-center gap-2">
        <Flower size={26} weight="duotone" className="text-secondary" />
        <h1 className="font-heading text-3xl font-light tracking-tight">Suggest a Memorial</h1>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Know someone from your school, college, university or alumni community who has passed away? Help us preserve their memory. Your suggestion goes to a review queue — it is <strong>not</strong> published as a confirmed memorial until reviewed.</p>

      <div className="mt-6 space-y-4 rounded-2xl border border-border/60 bg-card p-5">
        <div className="space-y-1.5"><Label>Person's name <span className="text-secondary">*</span></Label><Input value={f.name} onChange={set("name")} data-testid="suggest-name" placeholder="e.g. John Smith" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Institution</Label><Input value={f.institution} onChange={set("institution")} data-testid="suggest-institution" placeholder="ABC High School" /></div>
          <div className="space-y-1.5"><Label>Class / batch</Label><Input value={f.class_batch} onChange={set("class_batch")} data-testid="suggest-classbatch" placeholder="1985" /></div>
        </div>
        <div className="space-y-1.5"><Label>Approximate years</Label><Input value={f.approx_years} onChange={set("approx_years")} data-testid="suggest-years" placeholder="e.g. attended 1981–1985" /></div>
        <div className="space-y-1.5"><Label>Your relationship</Label><Input value={f.relationship} onChange={set("relationship")} data-testid="suggest-relationship" placeholder="Classmate, friend, teacher…" /></div>
        <div className="space-y-1.5"><Label>What you know</Label><Textarea value={f.known_info} onChange={set("known_info")} className="min-h-[80px]" data-testid="suggest-known" placeholder="e.g. I believe John passed away in 2024." /></div>
        <div className="space-y-1.5"><Label>Source (optional)</Label><Input value={f.source} onChange={set("source")} data-testid="suggest-source" placeholder="Obituary link, announcement, etc." /></div>

        <div className="flex items-start gap-2 rounded-xl bg-muted/30 p-3 text-xs text-muted-foreground">
          <Warning size={16} className="mt-0.5 shrink-0" /> Never suggest a memorial for a living person. Suggestions are reviewed before any memorial is published.
        </div>
        <Button className="w-full rounded-full" onClick={submit} disabled={loading} data-testid="suggest-submit">{loading ? "Submitting…" : "Submit suggestion"}</Button>
      </div>
    </div>
  );
}
